import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import WebsiteFeature from '../components/WebsiteFeature';

const {FiArrowUpRight, FiCheck, FiMapPin, FiStar, FiMessageCircle, FiGlobe, FiInstagram, FiShield, FiArrowRight, FiMail} = FiIcons;

const inclusions = [
  {icon: FiMapPin, title: 'Show up where it matters', description: 'Google Business Profile optimization, weekly updates and business citations help customers find accurate information about you.'},
  {icon: FiStar, title: 'Build a stronger reputation', description: 'Give happy customers an easier way to share their experience with the Easy Reviewz app.'},
  {icon: FiInstagram, title: 'Stay top of mind', description: 'Monthly Facebook and Instagram posting keeps your business active beyond search.'},
  {icon: FiGlobe, title: 'A custom SEO & AEO website', description: 'Get a professional custom website built to help customers find your business and answer engines understand it.'}
];

const deliverables = [
  'Professional custom website built with SEO and AEO in mind',
  'Google Business Profile optimization (description, services, attributes, and links)',
  'Weekly Google posts, written and published for you',
  'Photo and video uploads, geotagged and keyword-tagged',
  'Review requests by text, email, or QR code, plus follow-ups',
  'Review replies written in your voice',
  'Facebook and Instagram posting',
  'Short promo videos published to YouTube and Google',
  'Business listings kept in sync across directories',
  'Monthly ranking report and weekly progress updates',
  'Website FAQ and schema so Google and AI search can read your business'
];

function SectionHeading({eyebrow, children, description}) {
  return (
    <div className="max-w-2xl">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#3fc0fa]">{eyebrow}</p>
      <h2 className="text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">{children}</h2>
      {description && <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">{description}</p>}
    </div>
  );
}

function InclusionCard({item, index}) {
  return (
    <motion.div initial={{opacity: 0, y: 18}} whileInView={{opacity: 1, y: 0}} viewport={{once: true}} transition={{delay: index * 0.07}} className="rounded-2xl border border-white/10 bg-[#19283d] p-7 transition-colors hover:border-[#3fc0fa]/50 sm:p-8">
      <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-xl bg-[#3fc0fa]/10">
        <SafeIcon icon={item.icon} className="h-6 w-6 text-[#3fc0fa]" />
      </div>
      <h3 className="text-xl font-bold text-white">{item.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-400">{item.description}</p>
    </motion.div>
  );
}

function PlanCard() {
  return (
    <motion.div initial={{opacity: 0, y: 24}} animate={{opacity: 1, y: 0}} transition={{duration: 0.6, delay: 0.12}} className="relative overflow-hidden rounded-[2rem] border border-[#3fc0fa]/40 bg-[#19283d] p-6 shadow-2xl sm:p-8 lg:p-10">
      <div className="absolute right-0 top-0 h-32 w-32 rounded-bl-full bg-[#3fc0fa]/10" aria-hidden="true" />
      <div className="relative">
        <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#3fc0fa]/30 bg-[#3fc0fa]/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#3fc0fa]">
          <SafeIcon icon={FiShield} className="h-4 w-4" /> One simple plan
        </div>
        <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl">Local Momentum</h2>
        <p className="mt-3 leading-relaxed text-slate-300">Your custom website, Google Business Profile, reviews, and local posts, managed for you.</p>
        <div className="mt-8 flex items-baseline gap-1 border-b border-white/10 pb-8">
          <span className="text-5xl font-black tracking-tight text-white sm:text-6xl">$399</span>
          <span className="text-lg text-slate-400">/month</span>
        </div>
        <p className="mt-8 text-sm font-bold uppercase tracking-[0.15em] text-[#3fc0fa]">What’s included</p>
        <ul className="mt-5 space-y-4">
          {deliverables.map((feature) => (
            <li key={feature} className="flex items-start gap-3 text-sm leading-relaxed text-slate-200 sm:text-base">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3fc0fa]/15">
                <SafeIcon icon={FiCheck} className="h-3 w-3 text-[#3fc0fa]" />
              </span>
              {feature}
            </li>
          ))}
        </ul>
        <a href="mailto:support@jellylocal.com?subject=Local%20Momentum%20plan" className="site-button mt-9 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-center font-bold">
          Ask about Local Momentum <SafeIcon icon={FiArrowUpRight} className="h-5 w-5" />
        </a>
        <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">A one-time implementation fee applies.</p>
      </div>
    </motion.div>
  );
}

export default function PricingPage() {
  return (
    <main className="min-h-screen bg-[#0b1423] text-white">
      <section className="overflow-hidden pb-20 pt-32 sm:pb-28 sm:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:gap-20 lg:px-8">
          <motion.div initial={{opacity: 0, y: 22}} animate={{opacity: 1, y: 0}} transition={{duration: 0.65}}>
            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#3fc0fa]/30 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#3fc0fa]">
              <span className="h-2 w-2 rounded-full bg-[#3fc0fa]" /> Straightforward pricing
            </div>
            <h1 className="max-w-2xl text-5xl font-black leading-[1.05] tracking-[-0.055em] sm:text-6xl xl:text-[5rem]">
              Local growth, <span className="text-[#3fc0fa]">without the guesswork.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-slate-300 sm:text-xl">One clear plan to help your business get found, earn trust and stay visible in your community—with a professional custom website built for SEO and AEO.</p>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
              <span className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-[#3fc0fa]" /> Managed for you</span>
              <span className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-[#3fc0fa]" /> No long-term contract</span>
            </div>
            <a href="#whats-included" className="site-outline mt-10 inline-flex items-center gap-3 rounded-full px-7 py-4 font-bold">
              See what’s included <SafeIcon icon={FiArrowRight} />
            </a>
          </motion.div>
          <PlanCard />
        </div>
      </section>
      <WebsiteFeature onPricingPage />
      <section id="whats-included" className="bg-[#101c2d] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Made for local businesses" description="The essentials work better together. Local Momentum combines a custom website, practical tools and ongoing support in one manageable plan.">
            A stronger presence. <span className="text-[#3fc0fa]">One connected approach.</span>
          </SectionHeading>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:gap-5">
            {inclusions.map((item, index) => <InclusionCard key={item.title} item={item} index={index} />)}
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] border border-white/10">
            <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&amp;fit=crop&amp;q=85&amp;w=1200" alt="Local business team planning their next steps" loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </div>
          <div>
            <SectionHeading eyebrow="A real partner" description="Your business is more than a profile or a post. We take care of the day-to-day details that support a credible, discoverable local presence, so you can stay focused on your customers.">
              More time for the work <span className="text-[#3fc0fa]">you do best.</span>
            </SectionHeading>
            <div className="mt-8 space-y-4 border-t border-white/10 pt-8">
              <p className="flex items-start gap-3 text-slate-300"><SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-[#3fc0fa]" /> A clear monthly price and a practical set of deliverables.</p>
              <p className="flex items-start gap-3 text-slate-300"><SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-[#3fc0fa]" /> A consistent presence across search and social channels.</p>
              <p className="flex items-start gap-3 text-slate-300"><SafeIcon icon={FiCheck} className="mt-1 shrink-0 text-[#3fc0fa]" /> Room to talk through your goals before getting started.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-8 rounded-[2rem] bg-[#3fc0fa] px-7 py-12 text-[#0b1423] sm:px-12 sm:py-16 lg:flex-row lg:items-center lg:px-16">
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em]"><SafeIcon icon={FiMessageCircle} /> Let’s talk growth</div>
              <h2 className="text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">Ready to get your business moving?</h2>
              <p className="mt-4 text-base leading-relaxed text-[#0b1423]/80 sm:text-lg">Tell us about your business and we’ll help you decide if Local Momentum is the right fit.</p>
            </div>
            <a href="mailto:support@jellylocal.com?subject=Local%20Momentum%20plan" className="inline-flex shrink-0 items-center justify-center gap-3 rounded-full bg-[#0b1423] px-8 py-5 font-bold text-white transition-colors hover:bg-[#22344d]">
              <SafeIcon icon={FiMail} /> Get in touch <SafeIcon icon={FiArrowUpRight} />
            </a>
          </div>
          <p className="mt-5 text-center text-xs text-slate-400">Results depend on your market, competition and business goals.</p>
          <div className="mt-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3fc0fa] transition-colors hover:text-white">
              Back to the homepage <SafeIcon icon={FiArrowUpRight} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}